import { theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Checkbox, Table, Tooltip } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import type { ColumnType } from 'antd/es/table'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VISIT } from 'screens/Rostering/constants'
import useAllocationList from 'screens/Rostering/hooks/useAllocationList'
import styled from 'styled-components'
import AllocationFilters, {
  type AllocationFiltersValue
} from './AllocationFilters'
import AvailabilityInfo from './AvailabilityInfo'
import MatchSkillsPopover from './MatchSkillsPopover'
import TeamMemberInfo from './TeamMemberInfo'

type CustomElementsProps = {
  children: ReactNode
}

type AllocationTableProps = {
  revisionId: string
  type: 'shift' | 'visit'
  addedIds: string[]
  setAddedIds: (ids: string[]) => void
  removedIds: string[]
  setRemovedIds: (ids: string[]) => void
  isAllocationEditable: boolean
}

export type MatchSkill = {
  matchCount: string
  areAllMatched: boolean
  skillDetails: SkillDetail[]
}

export type SkillDetail = {
  skillId: string
  name: string
  selected: boolean
}

const AllocationTable = ({
  revisionId,
  type,
  addedIds,
  setAddedIds,
  removedIds,
  setRemovedIds,
  isAllocationEditable
}: AllocationTableProps) => {
  const pageSizes = [10, 20, 50, 100]

  const { t } = useTranslation()

  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([])

  const [filterParams, setFilterParams] = useState<AllocationFiltersValue>({})

  const {
    allocationList,
    numberOfSupply,
    selectedUsers,
    isListLoading,
    pagination,
    setPage
  } = useAllocationList({
    id: revisionId,
    allocationListFilters: filterParams
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setInitialSelectedIds([])
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setInitialSelectedIds((prevInitialSelectedIds) => {
      const newInitialSelectedIds = [...prevInitialSelectedIds]
      allocationList.forEach((member) => {
        const index = newInitialSelectedIds.indexOf(member.id)

        if (member.selected) {
          if (index === -1) {
            newInitialSelectedIds.push(member.id)
          }
        } else if (index !== -1) {
          newInitialSelectedIds.splice(index, 1)
        }
      })
      return newInitialSelectedIds
    })
  }, [allocationList])

  const handleCheckboxChanged = (e: CheckboxChangeEvent, id: string) => {
    if (e.target.checked) {
      if (!initialSelectedIds.includes(id)) {
        setAddedIds([...addedIds, id])
      }
      setRemovedIds(removedIds.filter((removedId) => removedId !== id))
    } else if (!e.target.checked) {
      if (initialSelectedIds.includes(id)) setRemovedIds([...removedIds, id])

      setAddedIds(addedIds.filter((addedId) => addedId !== id))
    }
  }

  const isSelected = (id: string) => {
    if (addedIds.includes(id)) return true
    if (removedIds.includes(id)) return false
    return initialSelectedIds.includes(id)
  }

  const columns: ColumnType<any>[] = [
    {
      key: 'checkbox',
      width: 50,
      render: (record: any) => (
        <CheckboxContainer>
          {(() => {
            const hasAbsences = record.absences?.length > 0
            const isClockedIn = record.clockedIn
            const isClockedInToOtherInstance = record.clockedInToOtherInstance
            const isClockedOut = record.clockedOut

            const isDisabled =
              hasAbsences ||
              isClockedIn ||
              isClockedInToOtherInstance ||
              isClockedOut ||
              !isAllocationEditable

            return isDisabled ? (
              <Tooltip
                placement="bottomRight"
                color={theme.color.neutral.color3}
                title={
                  <ContentContainer>
                    {hasAbsences && (
                      <Text>{t('CANNOT_SELECT_ABSENT_PEOPLE')}</Text>
                    )}
                    {isClockedIn && <Text>{t('CLOCKED_IN_TO_INSTANCE')}</Text>}
                    {isClockedOut && (
                      <Text>{t('CLOCKED_OUT_FROM_INSTANCE')}</Text>
                    )}
                    {isClockedInToOtherInstance && (
                      <Text>{t('CLOCKED_IN_TO_OTHER_INSTANCE')}</Text>
                    )}
                    {!isAllocationEditable && (
                      <Text>{t('CANNOT_EDIT_ALLOCATION_STATUS')}</Text>
                    )}
                  </ContentContainer>
                }
              >
                <Checkbox
                  disabled={isDisabled}
                  checked={isSelected(record.id)}
                />
              </Tooltip>
            ) : (
              <Checkbox
                checked={isSelected(record.id)}
                onChange={(e) => handleCheckboxChanged(e, record.id)}
              />
            )
          })()}
        </CheckboxContainer>
      )
    },
    {
      title: (
        <SelectedText weight="medium">{`${
          selectedUsers + addedIds.length - removedIds.length
        }/${numberOfSupply} ${t('SELECTED')}`}</SelectedText>
      ),
      key: 'teamMemberInfo',
      render: (record: any) => (
        <CellContainer width={120}>
          <TeamMemberInfo user={record} />
        </CellContainer>
      )
    },
    {
      title: (
        <HeaderContainer>
          <Text weight="medium">{t('AVAILABILITY')}</Text>
        </HeaderContainer>
      ),
      key: 'availability',
      render: (record: any) => (
        <CellContainer>
          <AvailabilityInfo user={record} />
        </CellContainer>
      )
    },
    ...(type === VISIT
      ? [
          {
            title: (
              <HeaderContainer>
                <Text weight="medium">{t('CARE_TEAM')}</Text>
              </HeaderContainer>
            ),
            dataIndex: 'careTeam',
            key: 'careTeam',
            render: (careTeam: string) =>
              careTeam === 'Any' ? (
                <Tooltip
                  placement="right"
                  color={theme.color.neutral.color3}
                  title={
                    <ContentContainer>
                      <Text>{t('ASSIGNED_TO_ALL')}</Text>
                    </ContentContainer>
                  }
                >
                  <CellCenterContainer>
                    <Text>{careTeam}</Text>
                  </CellCenterContainer>
                </Tooltip>
              ) : (
                <GreenContainer green={careTeam === 'Yes'}>
                  <Text>{careTeam}</Text>
                </GreenContainer>
              )
          }
        ]
      : []),
    {
      title: (
        <SkillsContainer>
          <Text weight="medium">{t('SKILLS')}</Text>
        </SkillsContainer>
      ),
      dataIndex: 'skills',
      key: 'skills',
      render: (skills: MatchSkill) => (
        <SkillsContainer green={skills.areAllMatched}>
          <MatchSkillsPopover skills={skills.skillDetails}>
            <Text>{skills.matchCount}</Text>
          </MatchSkillsPopover>
        </SkillsContainer>
      )
    }
  ]

  const CustomCell = ({ children, ...props }: CustomElementsProps) => {
    return <CustomCellStyled {...props}>{children}</CustomCellStyled>
  }

  const components = {
    body: {
      cell: CustomCell
    }
  }

  const getRowClassName = (record: any, index: number) => {
    return index % 2 === 0 ? 'even-row' : 'odd-row'
  }

  return (
    <SectionContainer>
      <AllocationFilters
        type={type}
        value={filterParams}
        onChange={setFilterParams}
      />
      <StyledTable<any>
        components={components}
        columns={columns}
        loading={isListLoading}
        dataSource={allocationList}
        rowClassName={getRowClassName}
        onChange={() => {}}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: true,
          pageSize: pagination?.itemsPerPage,
          hideOnSinglePage: false,
          pageSizeOptions: pageSizes,
          total: pagination?.totalItems,
          current: pagination?.currentPage,
          onChange: (page: number, size: number) => {
            setPage({ size, index: page })
          }
        }}
      />
    </SectionContainer>
  )
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledTable = styled(Table)`
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  height: 100%;

  & .even-row {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  & .odd-row {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }
` as unknown as typeof Table

const CustomCellStyled = styled.td`
  padding: 16px 12px !important;
  background-color: inherit !important;
`
const CellContainer = styled.div<{ width?: number }>`
  width: ${(props) => props.width}px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellCenterContainer = styled(CellContainer)`
  display: flex;
  justify-content: center;
`

const CheckboxContainer = styled(CellContainer)`
  padding: 0 24px;
  width: fit-content;
`

const HeaderContainer = styled(CellContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const GreenContainer = styled(CellContainer)<{ green?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${(props) =>
    props.green
      ? props.theme.color.status.success
      : props.theme.color.neutral.color3};
`
const SkillsContainer = styled(GreenContainer)`
  padding-right: 100px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 8px;
  color: ${(props) => props.theme.color.neutral.color11};
`

const SelectedText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default AllocationTable
